import React, { useEffect, useState } from "react";
import { Card, CardContent, Grid, Typography } from "@material-ui/core";
import {
  ChatBubbleOutline as ChatIcon,
  ShoppingCart as CartIcon,
  TrendingUp as TrendingIcon,
  AttachMoney as MoneyIcon,
} from "@material-ui/icons";
import { apiFetchChatAnalytics } from "../api/index.js";
import socket from "../utilities/socket";

const StatCard = ({ icon, label, value, sub, color }) => (
  <Card style={{ borderRadius: 12, height: "100%" }}>
    <CardContent style={{ display: "flex", alignItems: "center", gap: 14 }}>
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 10,
          background: `${color}1a`,
          color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: "0 0 44px",
        }}
      >
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <Typography variant="h5" style={{ fontWeight: 700, lineHeight: 1.15, fontSize: 22 }}>
          {value}
        </Typography>
        <Typography variant="body2" style={{ color: "#64748b" }}>
          {label}
        </Typography>
        {sub != null && (
          <Typography variant="caption" style={{ color: "#94a3b8" }}>
            {sub}
          </Typography>
        )}
      </div>
    </CardContent>
  </Card>
);

const ChatAnalytics = ({ vendorId, scope }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!vendorId) return undefined;
    let active = true;
    const load = () =>
      apiFetchChatAnalytics(vendorId, scope)
        .then(({ data }) => active && setData(data.analytics))
        .catch(() => active && setError("Chat analytics unavailable"));
    load();

    // Real-time: refetch the moment a chat/order event lands, with a short
    // debounce so a burst of events triggers a single reload.
    let debounce = null;
    const refresh = () => {
      clearTimeout(debounce);
      debounce = setTimeout(load, 800);
    };
    socket.emit("joinVendorInbox", { vendorId });
    socket.on("inbox:message_received", refresh);
    socket.on("inbox:message_sent", refresh);
    socket.on("inbox:order_created", refresh);
    socket.on("inbox:conversation_closed", refresh);
    socket.on("inbox:conversation_assigned", refresh);

    // 30s poll as a fallback in case a socket event is missed.
    const interval = setInterval(load, 30000);
    return () => {
      active = false;
      clearInterval(interval);
      clearTimeout(debounce);
      socket.emit("leaveVendorInbox", { vendorId });
      socket.off("inbox:message_received", refresh);
      socket.off("inbox:message_sent", refresh);
      socket.off("inbox:order_created", refresh);
      socket.off("inbox:conversation_closed", refresh);
      socket.off("inbox:conversation_assigned", refresh);
    };
  }, [vendorId, scope]);

  if (!vendorId || error) return null;

  const a = data || {
    totalConversations: 0,
    openConversations: 0,
    closedConversations: 0,
    byChannel: { whatsapp: 0, lusuku_app: 0 },
    ordersFromChat: 0,
    chatGmv: 0,
    conversionRate: 0,
    trend7d: [],
    responseTime: { avgFirstResponseMinutes: null, medianFirstResponseMinutes: null, conversationsMeasured: 0 },
    paymentFunnel: { created: 0, paid: 0, dispatched: 0, delivered: 0 },
  };

  const maxTrend = Math.max(1, ...a.trend7d.map((t) => t.count));
  const fmt = (n) => Number(n || 0).toLocaleString();
  const fmtMins = (m) => {
    if (m == null) return "—";
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    const r = m % 60;
    return r ? `${h}h ${r}m` : `${h}h`;
  };
  const rt = a.responseTime || {};
  const pf = a.paymentFunnel || {};
  const FunnelStep = ({ value, label }) => (
    <div style={{ textAlign: "center", minWidth: 64 }}>
      <Typography variant="h5" style={{ fontWeight: 800, fontSize: 22 }}>{fmt(value)}</Typography>
      <Typography variant="caption" style={{ color: "#64748b" }}>{label}</Typography>
    </div>
  );

  return (
    <div style={{ marginTop: 24 }}>
      <Card
        style={{
          background: "linear-gradient(135deg, #075e54 0%, #128c7e 100%)",
          color: "#fff",
          marginBottom: 16,
          borderRadius: 12,
        }}
      >
        <CardContent style={{ padding: "14px 20px", display: "flex", alignItems: "center", gap: 10 }}>
          <ChatIcon />
          <Typography variant="h6" style={{ fontWeight: 700, fontSize: 18 }}>
            Chat Analytics
          </Typography>
          <Typography variant="body2" style={{ marginLeft: "auto", opacity: 0.85 }}>
            WhatsApp & app conversations
          </Typography>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<ChatIcon />}
            color="#075e54"
            value={fmt(a.totalConversations)}
            label="Total conversations"
            sub={`${fmt(a.openConversations)} open · ${fmt(a.closedConversations)} closed`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<CartIcon />}
            color="#2563eb"
            value={fmt(a.ordersFromChat)}
            label="Orders from chat"
            sub={`${fmt(a.conversationsWithOrder)} chats converted`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<TrendingIcon />}
            color="#9333ea"
            value={`${a.conversionRate}%`}
            label="Conversion rate"
            sub="chats → orders"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<MoneyIcon />}
            color="#16a34a"
            value={`UGX ${fmt(a.chatGmv)}`}
            label="GMV from chat"
            sub="Gross sales · excl. rejected"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} style={{ marginTop: 2 }}>
        <Grid item xs={12} md={4}>
          <Card style={{ borderRadius: 12, height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle2" style={{ color: "#64748b", marginBottom: 12 }}>
                First-response time
              </Typography>
              <div style={{ display: "flex", gap: 20 }}>
                <div>
                  <Typography variant="h5" style={{ fontWeight: 800, fontSize: 22 }}>{fmtMins(rt.avgFirstResponseMinutes)}</Typography>
                  <Typography variant="caption" style={{ color: "#94a3b8" }}>Average</Typography>
                </div>
                <div>
                  <Typography variant="h5" style={{ fontWeight: 800, fontSize: 22 }}>{fmtMins(rt.medianFirstResponseMinutes)}</Typography>
                  <Typography variant="caption" style={{ color: "#94a3b8" }}>Median</Typography>
                </div>
              </div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={8}>
          <Card style={{ borderRadius: 12, height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle2" style={{ color: "#64748b", marginBottom: 12 }}>
                Chat order funnel — payment to delivery
              </Typography>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <FunnelStep value={pf.created} label="Created" />
                <span style={{ color: "#cbd5e1" }}>→</span>
                <FunnelStep value={pf.paid} label="Paid" />
                <span style={{ color: "#cbd5e1" }}>→</span>
                <FunnelStep value={pf.dispatched} label="Dispatched" />
                <span style={{ color: "#cbd5e1" }}>→</span>
                <FunnelStep value={pf.delivered} label="Delivered" />
              </div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2} style={{ marginTop: 2 }}>
        <Grid item xs={12} md={8}>
          <Card style={{ borderRadius: 12 }}>
            <CardContent>
              <Typography variant="subtitle2" style={{ color: "#64748b", marginBottom: 12 }}>
                New conversations — last 7 days
              </Typography>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 10, height: 120 }}>
                {a.trend7d.length === 0 && (
                  <Typography variant="body2" style={{ color: "#94a3b8" }}>
                    No conversations yet.
                  </Typography>
                )}
                {a.trend7d.map((t) => (
                  <div key={t.date} style={{ flex: 1, textAlign: "center" }}>
                    <div
                      title={`${t.count} on ${t.date}`}
                      style={{
                        height: `${(t.count / maxTrend) * 90}px`,
                        background: "#25d366",
                        borderRadius: "6px 6px 0 0",
                        minHeight: 4,
                      }}
                    />
                    <Typography variant="caption" style={{ color: "#94a3b8" }}>
                      {t.date.slice(5)}
                    </Typography>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card style={{ borderRadius: 12, height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle2" style={{ color: "#64748b", marginBottom: 12 }}>
                By channel
              </Typography>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
                <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: "50%", background: "#25d366", marginRight: 8 }} />WhatsApp</span>
                <strong>{fmt(a.byChannel.whatsapp)}</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
                <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: "50%", background: "#ef4444", marginRight: 8 }} />Lusuku App</span>
                <strong>{fmt(a.byChannel.lusuku_app)}</strong>
              </div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

export default ChatAnalytics;
